/**
 * Main PDP only. Listens for predictive-bot:nudge, shows nudgeText for 10s,
 * highlights sectionId, and opens Helium chat with chatPrefillText on click of
 * the nudge or the bot icon ([data-ask-ai]) while a nudge is active.
 */
(function () {
  var NUDGE_MS = 6000;
  var hideTimer = 0;
  var active = null;
  var highlighted = null;
  var highlightEl = null;
  var nudgeEl = null;
  var hidingNudge = false;
  var pendingNudge = "";
  var pendingSource = "";
  var retryTimer = 0;
  var clickTracked = false;
  var openTracked = false;
  var relayedHelium = typeof WeakSet === "function" ? new WeakSet() : [];

  function track(eventName, extra) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: eventName };
    if (extra) {
      for (var key in extra) {
        if (Object.prototype.hasOwnProperty.call(extra, key)) {
          payload[key] = extra[key];
        }
      }
    }
    window.dataLayer.push(payload);
  }

  function cssEscape(value) {
    var text = String(value);
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(text);
    }
    return text.replace(/[^a-zA-Z0-9_-]/g, "\\$&");
  }

  function findSection(sectionId) {
    if (!sectionId) return null;
    var id = String(sectionId);
    var escaped = cssEscape(id);
    return (
      document.getElementById(id) ||
      document.getElementById("shopify-section-" + id) ||
      document.querySelector('[data-section-id="' + escaped + '"]') ||
      document.querySelector('[id$="' + escaped + '"]')
    );
  }

  function clearHighlight() {
    if (highlightEl && highlightEl.parentNode) highlightEl.parentNode.removeChild(highlightEl);
    highlightEl = null;
    if (!highlighted) return;
    highlighted.classList.remove("is-predictive-nudge-target");
    highlighted = null;
  }

  function previewSection() {
    return (
      document.querySelector(".shopify-section.hdt-main-section--product") ||
      document.querySelector(".hdt-main-section--product") ||
      document.querySelector("#MainContent .shopify-section")
    );
  }

  function highlightSection(sectionId, isPreview) {
    clearHighlight();
    var section = findSection(sectionId);
    if (!section && isPreview) section = previewSection();
    if (!section) return;
    section.classList.add("is-predictive-nudge-target");
    highlightEl = document.createElement("div");
    highlightEl.className = "predictive-nudge-highlight";
    highlightEl.setAttribute("aria-hidden", "true");
    section.appendChild(highlightEl);
    highlighted = section;
  }

  function cssNumber(name, fallback) {
    var raw = getComputedStyle(document.documentElement).getPropertyValue(name);
    var value = parseFloat(raw);
    return isNaN(value) ? fallback : value;
  }

  function placeNudge() {
    if (!nudgeEl || !active) return;
    var widget = document.querySelector("[data-ask-ai]");
    if (!widget) return;
    var style = getComputedStyle(widget);
    var widgetLeft = parseFloat(style.left);
    var widgetBottom = parseFloat(style.bottom);
    if (!isFinite(widgetLeft)) widgetLeft = 16;
    if (!isFinite(widgetBottom)) widgetBottom = 16;

    var offsetX = cssNumber("--predictive-nudge-offset-x", 64);
    var offsetY = cssNumber("--predictive-nudge-offset-y", 0);
    var widgetHeight = widget.offsetHeight || 64;
    var nudgeHeight = nudgeEl.offsetHeight || 38;
    var left = Math.round(widgetLeft + offsetX);
    var bottom = Math.round(widgetBottom + (widgetHeight - nudgeHeight) / 2 - offsetY);
    var maxLeft = window.innerWidth - (nudgeEl.offsetWidth || 209) - 8;
    if (left > maxLeft) left = Math.max(8, maxLeft);
    if (nudgeEl._anchorLeft === left && nudgeEl._anchorBottom === bottom) return;

    nudgeEl._anchorLeft = left;
    nudgeEl._anchorBottom = bottom;
    nudgeEl.style.left = left + "px";
    nudgeEl.style.bottom = bottom + "px";
    nudgeEl.style.top = "auto";
  }

  function watchPlacement() {
    window.addEventListener("resize", placeNudge, { passive: true });
    window.addEventListener("orientationchange", placeNudge, { passive: true });
    var widget = document.querySelector("[data-ask-ai]");
    if (widget && window.MutationObserver) {
      var observer = new MutationObserver(placeNudge);
      observer.observe(widget, { attributes: true, attributeFilter: ["style", "class"] });
    }
  }

  function removeNudgeEl() {
    if (nudgeEl && nudgeEl.parentNode) nudgeEl.parentNode.removeChild(nudgeEl);
    nudgeEl = null;
    hidingNudge = false;
  }

  function motionOff() {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function hideNudge(reason, instant) {
    if (hidingNudge && !instant) return;
    if (!active && !nudgeEl) return;
    var shown = active;
    active = null;
    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = 0;
    }
    clearHighlight();

    function finish() {
      removeNudgeEl();
      if (!shown) return;
      track("chatbotNudgeHide", {
        nudge: shown.nudgeText,
        sectionId: shown.sectionId,
        reason: reason || "timeout",
      });
      shown = null;
    }

    if (instant || motionOff() || !nudgeEl) {
      finish();
      return;
    }

    hidingNudge = true;
    var el = nudgeEl;
    el.classList.remove("is-in");
    el.classList.add("is-out");
    var settled = false;
    function done() {
      if (settled) return;
      settled = true;
      finish();
    }
    el.addEventListener("transitionend", done);
    window.setTimeout(done, 320);
  }

  function heliumName(entry) {
    if (!entry || typeof entry !== "object") return "";
    if (entry.event === "helium_chat_open" || entry.event === "helium_chat_close") return entry.event;
    if (entry[0] === "event" && (entry[1] === "helium_chat_open" || entry[1] === "helium_chat_close")) {
      return entry[1];
    }
    return "";
  }

  function heliumParams(entry) {
    if (entry && (entry.event === "helium_chat_open" || entry.event === "helium_chat_close")) return entry;
    return (entry && entry[2]) || {};
  }

  function alreadyRelayed(entry) {
    if (relayedHelium instanceof WeakSet) return relayedHelium.has(entry);
    return relayedHelium.indexOf(entry) !== -1;
  }

  function markRelayed(entry) {
    if (relayedHelium instanceof WeakSet) relayedHelium.add(entry);
    else relayedHelium.push(entry);
  }

  function trackNudgeClicked(nudge, source) {
    if (clickTracked) return;
    clickTracked = true;
    track("dynamic_nudge_clicked", { nudge: nudge || "", source: source || "" });
  }

  function trackChatOpen(nudge, source) {
    if (openTracked) return;
    openTracked = true;
    track("chat_window_open", { nudge: nudge || "", source: source || "" });
  }

  function relayHeliumEvent(entry) {
    var name = heliumName(entry);
    if (!name || alreadyRelayed(entry)) return;
    markRelayed(entry);

    var params = heliumParams(entry);
    var nudge = params.nudge || pendingNudge || "";
    if (name === "helium_chat_open") {
      trackChatOpen(nudge, pendingSource);
      trackNudgeClicked(nudge, pendingSource);
      pendingNudge = "";
      pendingSource = "";
      return;
    }
    track("chat_window_closed", { nudge: nudge });
  }

  function watchHeliumEvents() {
    var layer = (window.dataLayer = window.dataLayer || []);
    if (!layer.push || !layer.push.__nudgeHelium) {
      var currentPush = layer.push;
      function hookedPush() {
        var result = currentPush.apply(this, arguments);
        for (var i = 0; i < arguments.length; i++) relayHeliumEvent(arguments[i]);
        return result;
      }
      hookedPush.__nudgeHelium = true;
      layer.push = hookedPush;
    }
    for (var n = 0; n < layer.length; n++) relayHeliumEvent(layer[n]);
  }

  function openHelium(prefill) {
    if (window.Helium && typeof window.Helium.openChat === "function") {
      window.Helium.openChat({ prefill: prefill });
      return true;
    }

    var host = document.getElementById("concierge-chatbot");
    var btn = host && host.querySelector("button");
    if (!btn && host && host.shadowRoot) btn = host.shadowRoot.querySelector("button");
    if (btn) {
      btn.click();
      return true;
    }
    if (window.$chatwoot && typeof window.$chatwoot.toggle === "function") {
      window.$chatwoot.toggle();
      return true;
    }
    return false;
  }

  function openHeliumWithRetry(prefill) {
    if (retryTimer) {
      window.clearInterval(retryTimer);
      retryTimer = 0;
    }
    if (openHelium(prefill)) return;
    var tries = 0;
    retryTimer = window.setInterval(function () {
      tries++;
      if (openHelium(prefill) || tries >= 20) {
        window.clearInterval(retryTimer);
        retryTimer = 0;
      }
    }, 250);
  }

  function onNudgeClick(source) {
    if (!active) return;
    var shown = active;
    var nudge = shown.nudgeText;
    pendingNudge = nudge;
    pendingSource = source;
    trackNudgeClicked(nudge, source);
    hideNudge("click");
    openHeliumWithRetry(shown.chatPrefillText);
    trackChatOpen(nudge, source);
  }

  function onAskAiClick(event) {
    var target = event.target;
    if (!active || !target || typeof target.closest !== "function") return;
    if (!target.closest("[data-ask-ai]")) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    onNudgeClick("bot_icon");
  }

  function showNudge(detail) {
    var nudgeText = detail && detail.nudgeText ? String(detail.nudgeText) : "";
    if (!nudgeText) return;

    if (active || nudgeEl) hideNudge("replaced", true);

    clickTracked = false;
    openTracked = false;
    active = {
      nudgeText: nudgeText,
      chatPrefillText: detail.chatPrefillText || "",
      sectionId: detail.sectionId || "",
      preview: !!detail.preview,
    };

    highlightSection(active.sectionId, active.preview);

    nudgeEl = document.createElement("div");
    nudgeEl.className = "predictive-nudge";
    nudgeEl.setAttribute("data-predictive-nudge", "");
    nudgeEl.setAttribute("role", "status");

    var textBtn = document.createElement("button");
    textBtn.type = "button";
    textBtn.className = "predictive-nudge__text";
    textBtn.textContent = nudgeText;
    textBtn.addEventListener("click", function () {
      onNudgeClick("tooltip");
    });

    var closeBtn = document.createElement("button");
    closeBtn.type = "button";
    closeBtn.className = "predictive-nudge__close";
    closeBtn.setAttribute("aria-label", "Close");
    closeBtn.textContent = "\u00d7";
    closeBtn.addEventListener("click", function (event) {
      event.stopPropagation();
      hideNudge("dismiss");
    });

    nudgeEl.appendChild(textBtn);
    nudgeEl.appendChild(closeBtn);
    document.body.appendChild(nudgeEl);
    placeNudge();
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (nudgeEl) nudgeEl.classList.add("is-in");
      });
    });
    window.setTimeout(placeNudge, 50);

    track("chatbotNudgeShown", {
      nudge: active.nudgeText,
      sectionId: active.sectionId,
    });

    hideTimer = window.setTimeout(function () {
      hideNudge("timeout");
    }, NUDGE_MS);
  }

  document.addEventListener("predictive-bot:nudge", function (event) {
    showNudge(event && event.detail);
  });
  document.addEventListener("click", onAskAiClick, true);

  function bootPreview() {
    if (!window.__predictiveNudgePreview) return;
    showNudge(window.__predictiveNudgePreview);
    window.setTimeout(placeNudge, 300);
    window.setTimeout(placeNudge, 800);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      watchPlacement();
      watchHeliumEvents();
      bootPreview();
    });
  } else {
    watchPlacement();
    watchHeliumEvents();
    bootPreview();
  }
  window.setInterval(watchHeliumEvents, 500);
})();
